// CodeViolationEvidence — "why is this a code-violation lead, and where can I
// check it?" panel on the property modal (Sprint 1 item 4).
//
// Shows only facts the API already returns on leadSignals: open-case count,
// order/hearing date, rollup stage in plain words, and a "View source" link to
// the record. The statute-backed caveat lives in RiskFlagList directly below
// this panel; the one-line caveat here is the same sentence the backend risk
// flag uses. Never renders owner or mailing data.
//
// Renders NOTHING for a property with no code_enforcement/condemnation signal.

import React from "react";
import { Property } from "../types";
import { CODE_VIOLATION_CAVEAT, codeViolationEvidence } from "../lib/codeViolationEvidence";

const CodeViolationEvidence: React.FC<{ property: Property }> = ({ property }) => {
  const ev = codeViolationEvidence(property);
  if (!ev) return null;

  return (
    <section className="cv-evidence" aria-label="Code-enforcement evidence" data-testid="cv-evidence">
      <h3 className="cv-evidence__title">Code-enforcement evidence</h3>
      <p className="cv-evidence__headline">{ev.headline}</p>
      <dl className="cv-evidence__facts">
        {ev.openCasesText && (
          <div className="cv-evidence__row">
            <dt>Cases</dt>
            <dd data-testid="cv-open-cases">{ev.openCasesText}</dd>
          </div>
        )}
        {ev.orderDateText && (
          <div className="cv-evidence__row">
            <dt>Order / hearing date</dt>
            <dd
              className="cv-evidence__mono"
              data-testid="cv-order-date"
              title="Most recent order, hearing or case date the source reports"
            >
              {ev.orderDateText}
            </dd>
          </div>
        )}
        {ev.stageText && (
          <div className="cv-evidence__row">
            <dt>Stage</dt>
            <dd data-testid="cv-stage">{ev.stageText}</dd>
          </div>
        )}
        {ev.subStatus && (
          <div className="cv-evidence__row">
            <dt>Status</dt>
            <dd>{ev.subStatus}</dd>
          </div>
        )}
      </dl>
      {ev.evidenceUrl && (
        <a
          className="cv-evidence__link"
          href={ev.evidenceUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="cv-evidence-link"
        >
          View source
          <span className="cv-evidence__sr"> (opens the municipal record in a new tab)</span>
        </a>
      )}
      <p className="cv-evidence__caveat">{CODE_VIOLATION_CAVEAT}</p>
    </section>
  );
};

export default CodeViolationEvidence;
